import React from 'react';

import { Formik, FormikValues } from 'formik';

import SaveIcon from '@material-ui/icons/Save';

import { useSnackbar } from 'notistack';

import { useStyles } from './Themable.hooks';

import TranslationHelper from '../../../../../helpers/TranslationHelper';

import { useAppDispatch, useToolkitDispatch } from '../../../../../hooks';

import { addLocationSchema } from '../../../../../schemas';

import PaneHeader from '../../../../../components/PaneHeader';
import IconButtonWithTooltip from '../../../../../components/IconButtonWithTooltip';
import LocationForm from '../../../../../features/LocationForm';
import {
    DEFAULT_VISIBILITY_TYPE,
    prepareGroupIds,
} from '../../../../../components/VisibilityForm/VisibilityForm';
import {
    useLocationPosition,
    useSelectedTaskId,
    useTasksMode,
} from '../../../../../state/ui/planning/tasks/index.hooks';
import { ICoordinates } from '../../../../../state/types';
import {
    enterTasksPreviewMode,
    resetTasksPaneMode,
    setLocationPosition,
} from '../../../../../state/ui/planning/tasks';
import { createLocation } from '../../../../../state/app/locations';
import { useUserDataPermissions } from '../../../../../state/user/index.hooks';
import { updateForm } from '../../../../../state/ui/forms';
import { showTopDialog } from '../../../../..//state/ui/dialog';

const PlanningTaskLocationForm = () => {
    const initialValues = {
        id: '',
        name: '',
        address: '',
        longitude: '',
        latitude: '',
        notes: '',
        visibilityType: DEFAULT_VISIBILITY_TYPE,
        groups: [],
    };

    const dispatch = useAppDispatch();
    const toolkitDispatch = useToolkitDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const mapCoordinates = useLocationPosition();
    const classes = useStyles();
    const mode = useTasksMode();
    const dataPermissions = useUserDataPermissions();
    const taskId = useSelectedTaskId();
    const showNotification = (success: boolean, message: string) => {
        enqueueSnackbar(message, {
            variant: success ? 'success' : 'error',
        });
    };

    const getRightCustomControls = (
        submitForm: () => void,
        submitting: boolean
    ) => (
        <IconButtonWithTooltip
            title={'Save'}
            onClick={submitForm}
            disabled={submitting}
        >
            <SaveIcon />
        </IconButtonWithTooltip>
    );

    const handleSubmit = (values: FormikValues) => {
        const [city, street, streetNumber] = values.address
            .split(',')
            .map((item: string) => item.trim());
        const data = {
            coordinate: {
                x: parseFloat(values.longitude),
                y: parseFloat(values.latitude),
            },
            name: values.name,
            address: {
                city,
                street,
                streetNumber,
            },
            notes: values.notes,
            clientId: undefined,
            groupIds: prepareGroupIds(values, dataPermissions),
        };
        dispatch(
            createLocation(
                data,
                (data) => {
                    dispatch(
                        updateForm({
                            type: 'task',
                            field: 'location',
                            value: data,
                        })
                    );
                    handleCloseClick();
                    showNotification(
                        true,
                        TranslationHelper.translate('Location has been added')
                    );
                },
                () => {
                    showNotification(
                        false,
                        TranslationHelper.translate(
                            'Unrecognized error has occurred'
                        )
                    );
                }
            )
        );
    };

    const handleStoreClickPosition = (coords: ICoordinates) => {
        toolkitDispatch(setLocationPosition(coords));
    };

    const handleCloseClick = () => {
        toolkitDispatch(setLocationPosition(null));
        if (mode === 'addLocation') {
            dispatch(showTopDialog());
        }
        taskId
            ? toolkitDispatch(enterTasksPreviewMode(taskId))
            : toolkitDispatch(resetTasksPaneMode());
    };
    const handleChangeMarkerPosition = (values: FormikValues) => {
        toolkitDispatch(
            setLocationPosition({
                x: Number(Number(values.longitude).toFixed(6)),
                y: Number(Number(values.latitude).toFixed(6)),
            })
        );
    };
    return (
        <div>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={addLocationSchema}
            >
                {({
                    submitForm,
                    isSubmitting,
                    errors,
                    values,
                    setFieldValue,
                }) => (
                    <>
                        <PaneHeader
                            title={TranslationHelper.translate('Add location')}
                            onCloseClick={handleCloseClick}
                            renderRightCustomControls={() =>
                                getRightCustomControls(submitForm, isSubmitting)
                            }
                        />
                        <div className={classes.formWrapper}>
                            <LocationForm
                                errors={errors}
                                values={values}
                                setFieldValue={setFieldValue}
                                showAlert={false}
                                mapCoordinates={mapCoordinates}
                                handleStoreClickPosition={
                                    handleStoreClickPosition
                                }
                                handleChangeMarkerPosition={
                                    handleChangeMarkerPosition
                                }
                            />
                        </div>
                    </>
                )}
            </Formik>
        </div>
    );
};

export default PlanningTaskLocationForm;
